"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { usePuck } from "@puckeditor/core";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useStudioFileAssets } from "@/app/studio/pages/[slug]/StudioFileAssetsContext";
import {
  isDefaultStudioEditingLang,
  useStudioEditing,
} from "@/app/studio/pages/[slug]/StudioEditingContext";
import {
  inspectStudioImageFile,
  type StudioImageInspection,
  type StudioImageSizeBand,
} from "@/utils/inspectStudioImageFile";
import {
  fileExtension,
  STUDIO_CONTENT_IMAGE_ALLOWED_EXTENSIONS,
} from "@/utils/studioBlobPath";

const ACCEPT = [...STUDIO_CONTENT_IMAGE_ALLOWED_EXTENSIONS]
  .map((ext) => `.${ext}`)
  .join(",");

type StudioImageFieldProps = {
  value: string;
  onChange: (value: string) => void;
};

function isImageAssetFilename(filename: string): boolean {
  return STUDIO_CONTENT_IMAGE_ALLOWED_EXTENSIONS.has(fileExtension(filename));
}

function sizeBandClass(band: StudioImageSizeBand): string {
  if (band === "green") return "text-gail-success";
  if (band === "yellow") return "text-amber-600";
  return "text-red-600";
}

function sizeBandLabel(band: StudioImageSizeBand): string {
  if (band === "green") return "Green";
  if (band === "yellow") return "Yellow";
  return "Red";
}

export function StudioImageField({
  value,
  onChange,
}: Readonly<StudioImageFieldProps>) {
  const { selectedItem } = usePuck();
  const { editingLang, getEnglishBlockProp } = useStudioEditing();
  const { assets, uploadFile, removeAsset, countReferences } =
    useStudioFileAssets();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingObjectUrlRef = useRef<string | null>(null);
  const previousSrcRef = useRef(value);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(
    null,
  );
  const [inspection, setInspection] = useState<StudioImageInspection | null>(
    null,
  );
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const blockId =
    typeof selectedItem?.props?.id === "string" ? selectedItem.props.id : null;
  const englishSrc = blockId ? getEnglishBlockProp(blockId, "src") : null;
  const isNonEnglish = !isDefaultStudioEditingLang(editingLang);
  const trimmedValue = typeof value === "string" ? value.trim() : "";
  const usingEnglishFallback =
    isNonEnglish &&
    Boolean(englishSrc) &&
    (!trimmedValue || trimmedValue === englishSrc);
  const hasLocaleOverride =
    isNonEnglish &&
    Boolean(englishSrc) &&
    Boolean(trimmedValue) &&
    trimmedValue !== englishSrc;

  const imageAssets = useMemo(
    () => assets.filter((asset) => isImageAssetFilename(asset.filename)),
    [assets],
  );

  const selectedAsset = imageAssets.find((asset) => asset.url === value);
  const canRemoveSelectedStudioAsset = Boolean(
    selectedAsset && !pendingPreviewUrl,
  );

  function revokePendingPreview() {
    if (pendingObjectUrlRef.current) {
      URL.revokeObjectURL(pendingObjectUrlRef.current);
      pendingObjectUrlRef.current = null;
    }
    setPendingPreviewUrl(null);
  }

  function clearPending() {
    const pendingUrl = pendingObjectUrlRef.current;
    revokePendingPreview();
    setPendingFile(null);
    setInspection(null);
    if (pendingUrl && value === pendingUrl) {
      onChange(previousSrcRef.current);
    }
  }

  useEffect(() => {
    return () => {
      if (pendingObjectUrlRef.current) {
        URL.revokeObjectURL(pendingObjectUrlRef.current);
      }
    };
  }, []);

  function handleFilePick(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setError(null);
    const report = inspectStudioImageFile({
      filename: file.name,
      sizeBytes: file.size,
    });
    if (!value.startsWith("blob:")) {
      previousSrcRef.current = value;
    }
    revokePendingPreview();
    const objectUrl = URL.createObjectURL(file);
    pendingObjectUrlRef.current = objectUrl;
    setPendingPreviewUrl(objectUrl);
    setPendingFile(file);
    setInspection(report);
    // Drive the Puck canvas with a local object URL until upload confirms.
    onChange(objectUrl);
  }

  async function handleConfirmUpload() {
    if (!pendingFile || !inspection?.canUpload) return;

    setUploading(true);
    setError(null);
    try {
      const asset = await uploadFile(pendingFile, "content-image");
      revokePendingPreview();
      setPendingFile(null);
      setInspection(null);
      previousSrcRef.current = asset.url;
      // Select the uploaded asset immediately (do not restore the pre-upload src).
      onChange(asset.url);
    } catch (uploadError) {
      setError(
        uploadError instanceof Error ? uploadError.message : "upload_failed",
      );
    } finally {
      setUploading(false);
    }
  }

  async function handleRemoveImage() {
    if (!selectedAsset || pendingPreviewUrl) return;

    const url = selectedAsset.url;
    const references = countReferences(url);
    setRemoving(true);
    setError(null);
    try {
      // Non-English locales fall back to the English image when cleared.
      const nextSrc = isNonEnglish && englishSrc ? englishSrc : "";
      onChange(nextSrc);
      previousSrcRef.current = nextSrc;
      // Only delete from storage when this was the sole page reference.
      if (references <= 1) {
        await removeAsset(url);
      }
    } catch (removeError) {
      setError(
        removeError instanceof Error ? removeError.message : "delete_failed",
      );
    } finally {
      setRemoving(false);
    }
  }

  function handleUseEnglishImage() {
    if (!englishSrc) return;
    onChange(englishSrc);
    previousSrcRef.current = englishSrc;
  }

  return (
    <div className="flex flex-col gap-2">
      {usingEnglishFallback ? (
        <p className="font-monument text-xs text-black/60">
          Using English image for {editingLang}. Upload to override this locale
          only.
        </p>
      ) : null}
      {hasLocaleOverride ? (
        <p className="font-monument text-xs text-black/60">
          Locale-specific image for {editingLang}.
        </p>
      ) : null}

      <Input
        value={pendingPreviewUrl ? previousSrcRef.current : value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="https://... or /images/..."
        className="font-mono text-xs"
        aria-label="Image URL"
        disabled={Boolean(pendingPreviewUrl)}
      />

      {imageAssets.length > 0 ? (
        <Select
          value={selectedAsset?.url ?? ""}
          onValueChange={(nextValue) => onChange(nextValue)}
        >
          <SelectTrigger className="font-monument text-xs">
            <SelectValue placeholder="Choose an uploaded image" />
          </SelectTrigger>
          <SelectContent>
            {imageAssets.map((asset) => (
              <SelectItem key={asset.id} value={asset.url}>
                {asset.filename}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={handleFilePick}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || removing}
          className="rounded-md border border-black/15 px-3 py-1.5 font-monument text-xs disabled:opacity-50"
        >
          {isNonEnglish ? `Upload ${editingLang} image…` : "Choose image…"}
        </button>
        {hasLocaleOverride ? (
          <button
            type="button"
            onClick={handleUseEnglishImage}
            disabled={uploading || removing}
            className="rounded-md border border-black/15 px-3 py-1.5 font-monument text-xs disabled:opacity-50"
          >
            Use English image
          </button>
        ) : null}
        {canRemoveSelectedStudioAsset ? (
          <button
            type="button"
            onClick={() => void handleRemoveImage()}
            disabled={removing || uploading}
            className="rounded-md border border-black/15 px-3 py-1.5 font-monument text-xs text-red-700 disabled:opacity-50"
          >
            {removing ? "Removing…" : "Remove image"}
          </button>
        ) : null}
      </div>

      {inspection && pendingFile ? (
        <div
          className="rounded-md border border-black/10 bg-black/3 p-3"
          role="status"
          aria-live="polite"
        >
          {pendingPreviewUrl ? (
            <div className="relative mb-3 overflow-hidden rounded-md border border-black/10 bg-gail-silver/30">
              {/* Local object-URL preview only; not the uploaded asset. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={pendingPreviewUrl}
                alt=""
                className="max-h-40 w-full object-contain opacity-50 grayscale"
              />
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/45">
                <p className="px-3 text-center font-monument text-xs font-semibold tracking-wide text-white">
                  Not uploaded yet
                </p>
              </div>
            </div>
          ) : null}
          <p className="font-monument text-xs font-semibold text-black">
            Quality report — review before upload
          </p>
          <p className="mt-1 font-mono text-[11px] text-black/60">
            {pendingFile.name}
          </p>
          <ul className="mt-2 flex flex-col gap-1">
            {inspection.messages.map((message) => (
              <li
                key={message}
                className="font-monument text-xs text-black/80"
              >
                {message}
              </li>
            ))}
          </ul>
          <p
            className={`mt-2 font-monument text-xs font-semibold ${sizeBandClass(inspection.sizeBand)}`}
          >
            Size band: {sizeBandLabel(inspection.sizeBand)}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void handleConfirmUpload()}
              disabled={uploading || !inspection.canUpload}
              className="rounded-md border border-black bg-black px-3 py-1.5 font-monument text-xs text-white disabled:opacity-50"
            >
              {uploading ? "Uploading…" : "Upload image"}
            </button>
            <button
              type="button"
              onClick={clearPending}
              disabled={uploading}
              className="rounded-md border border-black/15 px-3 py-1.5 font-monument text-xs disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <p className="font-monument text-xs text-red-600" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
